/**
 * Perena API Types
 * Defines interfaces for perena-related API operations
 */

import { env } from "@/lib/env";

// Base API Response
export interface PerenaApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// Price Types
export interface PriceData {
  price: number;
  token: string;
  timestamp: string;
}

export interface GetPriceResponse {
  success: boolean;
  data: PriceData;
  error?: string;
}

// Quote Types
export interface MintQuoteRequest {
  usdcAmount: number;
  yieldingMint: string;
}

export interface BurnQuoteRequest {
  usdStarAmount: number;
  yieldingMint: string;
}

export interface QuoteData {
  inputAmount: number;
  outputAmount: number;
  inputToken: string;
  outputToken: string;
  exchangeRate: number;
  fee: number;
}

export interface QuoteResponse {
  success: boolean;
  data: QuoteData;
  error?: string;
}

// Stats Types
export interface StatsData {
  usdStarPrice: number;
  environment: string;
  cluster: string;
  rpcUrl: string;
}

export interface GetStatsResponse {
  success: boolean;
  data: StatsData;
  error?: string;
}

// Mint Types
export interface MintRequest {
  walletId: string;
  walletAddress: string;
  usdcAmount: number;
}

export interface MintData {
  hash: string;
  expectedOutput: number;
  inputAmount: number;
  inputToken: string;
  outputToken: string;
}

export interface MintResponse {
  success: boolean;
  data: MintData;
  error?: string;
}

// Burn Types
export interface BurnRequest {
  walletId: string;
  walletAddress: string;
  usdStarAmount: number;
}

export interface BurnData {
  hash: string;
  expectedOutput: number;
  inputAmount: number;
  inputToken: string;
  outputToken: string;
}

export interface BurnResponse {
  success: boolean;
  data: BurnData;
  error?: string;
}

// APY Types
export interface ApyData {
  period: string;
  apy: number;
  timestamp: string;
}

export interface GetApyResponse {
  success: boolean;
  data: ApyData;
  error?: string;
}

// Price API Types (updated to match new endpoint)
export interface PriceApiData {
  price: number;
  timestamp: string;
}

export interface GetPriceApiResponse {
  success: boolean;
  data: PriceApiData;
  error?: string;
}

// Portfolio Types
export interface PortfolioRequest {
  wallet: string;
}

export interface PortfolioData {
  earnings: number;
  usdStarBalance: number;
  currentPrice: number;
  netValue: number;
  netValueFormatted: string;
}

export interface PortfolioResponse {
  success: boolean;
  data: PortfolioData;
  error?: string;
}

// Yield Types
export interface WeeklyYieldData {
  yieldAmount: number;
  earnings?: number;
  earningsFormatted?: string;
  currentBalance?: number;
  currentDay?: number;
  weekStart?: string;
  weekEnd?: string;
  priceAtWeekStart?: number;
  currentPrice?: number;
  priceChange?: number;
  priceChangeFormatted?: string;
}

export interface WeeklyYieldResponse {
  success: boolean;
  data: WeeklyYieldData;
  error?: string;
}

// Configuration
export interface PerenaConfig {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
}

export const PERENA_CONFIG: PerenaConfig = {
  baseUrl: env.VITE_API_BASE_URL,
  timeout: 100000,
  retryAttempts: 3,
};
